import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GoogleChartInterface, GoogleChartType } from 'ng2-google-charts';
import { WebsiteAuditService } from '../website-audit.service';
import { DarkmodeService } from 'src/app/services/darkmode.service';

const SECTION_KEY = 'behavioralScience';

const ACCENT = '#6161ff';
const COMP_COLOR = '#e0a544';

// Distinct line colors for each tracked competitor in the trend chart (matches
// the Overview/category palette). COMP_COLOR (amber) stays first.
const COMP_PALETTE = [
  '#e0a544',
  '#5b9df0',
  '#c98ae0',
  '#5fcf95',
  '#e26a5d',
  '#4da18c',
  '#dd7bb0',
  '#c8e6a0',
];

// The API ships a mechanism name + cluster but no definition, so describe the
// mechanisms locally (same fallback approach as the Technical sub-categories).
// Keys are normalized (lowercase, alphanumeric words) — see normKey().
const MECHANISM_DESC: { [k: string]: string } = {
  scarcity:
    'Perceived limits on availability that shorten decision time and reduce hesitation.',
  'social proof':
    'Evidence that other shoppers chose this, which lowers uncertainty about the decision.',
  trust: 'Credibility signals that reduce the perceived risk of buying.',
  'trust authority':
    'Credibility and expertise signals that reduce the perceived risk of buying.',
  authority:
    'Signals of expertise or endorsement that make claims easier to accept.',
  'cognitive ease fluency':
    'How little mental effort the page demands — easier pages feel faster and more trustworthy.',
  fluency:
    'How effortlessly the page can be read and acted on; fluent experiences feel more trustworthy.',
  'cognitive ease':
    'How little mental effort the page demands — easier pages feel faster and more trustworthy.',
  'anchoring framing':
    'Reference points that shape how price and value are judged.',
  anchoring: 'Reference points that shape how price and value are judged.',
  commitment:
    'Small early steps that make completing the larger action more likely.',
  'loss aversion':
    'Framing that stresses what is lost by not acting — felt more strongly than an equal gain.',
  reciprocity:
    'Giving something of value first, which creates a pull to give back.',
  'endowment effect':
    'Making the product feel already owned, which raises its perceived value.',
  urgency:
    'Time pressure that shortens deliberation and brings the decision forward.',
};

// Cluster-level fallback when the mechanism name isn't in the map above.
const CLUSTER_DESC: { [k: string]: string } = {
  'social proof':
    'Signals that other shoppers already chose this, reducing decision uncertainty.',
  'urgency loss':
    'Time and availability pressure, plus what the shopper stands to lose by waiting.',
  'trust authority':
    'Credibility signals that lower the perceived risk of transacting.',
  'cognitive ease':
    'How much mental effort the experience demands at each step.',
  'anchoring framing':
    'The reference points and wording that shape perceived value.',
  commitment:
    'How the flow builds up small commitments toward the final action.',
};

@Component({
  selector: 'app-behavioral-science',
  templateUrl: './behavioral-science.component.html',
  // Reuses the category layout (header card, benchmark bars, list + detail
  // grid) so this tab looks identical to UX / CR / Technical; the local sheet
  // only adds the behavioral-specific pieces.
  styleUrls: [
    '../audit-theme.css',
    '../category/audit-category.component.css',
    './behavioral-science.component.css',
  ],
})
export class BehavioralScienceComponent implements OnInit, OnDestroy {
  constructor(
    private websiteAuditService: WebsiteAuditService,
    private darkmodeService: DarkmodeService,
  ) {}

  private subs = new Subscription();
  // Overlay shown while the sub-bar swaps competitors.
  public competitorSwitching$ =
    this.websiteAuditService.getCompetitorSwitching();

  accent = ACCENT;
  compColor = COMP_COLOR;

  // semantic colors — theme-aware
  green = '#5fcf95';
  red = '#e26a5d';
  amber = '#e0a544';
  gray = '#6b6660';

  private isDark = true;

  public isLoading = false;
  public hasData = false;

  public selectedTask: any;
  private section: any = null;
  private compSection: any = null;

  // header
  public label = 'Behavioral Science';
  public description = '';
  public score: number | null = null;
  public compName = '';
  public hasComp = false;
  public comp: number | null = null;
  public leadText = '';
  public leadColor = '';

  // header stats (screenshot: score · mechanisms active · largest gap)
  public activeCount = 0;
  public totalCount = 0;
  public largestGapText = '';

  // benchmark bars (your site + every competitor, ranked highest first)
  public benchBars: {
    label: string;
    score: number;
    width: string;
    color: string;
    isYou?: boolean;
  }[] = [];

  // cluster filter chips
  public clusters: { key: string; label: string; count: number }[] = [];
  public selectedCluster = 'all';

  // mechanisms
  public mechanisms: any[] = [];
  public visibleMechanisms: any[] = [];
  public selectedMechanism: any = null;
  // Which side the detail columns describe — your site or the competitor.
  public detailScope: 'target' | 'competitor' = 'target';

  // supporting blocks
  public topOpportunities: any[] = [];

  // trend (project-standard Google LineChart)
  public trendHasData = false;
  public trendLegend: {
    key: string;
    label: string;
    color: string;
    isYou: boolean;
    visible: boolean;
  }[] = [];
  private trendVisible: { [key: string]: boolean } = {};
  public trendLineChart: GoogleChartInterface = {
    chartType: GoogleChartType.LineChart,
    dataTable: [],
    options: {
      legend: {
        position: 'top',
        alignment: 'center',
        textStyle: {
          fontSize: 12,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
      },
      pointSize: 4,
      interpolateNulls: true,
      crosshair: {
        trigger: 'focus',
        orientation: 'vertical',
        color: '#eeeeee',
      },
      focusTarget: 'category',
      tooltip: { textStyle: { fontSize: 12 }, isHtml: true, trigger: 'focus' },
      colors: [ACCENT, COMP_COLOR],
      height: 240,
      backgroundColor: 'transparent',
      chartArea: {
        left: 40,
        right: 16,
        top: 30,
        bottom: 36,
        width: '100%',
        height: '70%',
      },
      vAxis: {
        minValue: 0,
        maxValue: 100,
        baseline: 0,
        baselineColor: '#c0c0c0',
        viewWindow: { min: 0, max: 100 },
        gridlineColor: '#ededed',
        textStyle: {
          fontSize: 11,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
      },
      hAxis: {
        textStyle: {
          fontSize: 11,
          fontName: 'Roboto, sans-serif',
          color: '#666',
        },
        gridlines: { color: 'transparent' },
        minorGridlines: { count: 0 },
        baselineColor: 'transparent',
      },
    },
  };

  ngOnInit(): void {
    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        // Follows the service value (the share shell sets it from the shared-with
        // theme), so the shared page matches the sharer's theme.
        this.applyThemeColors(!!isDark);
        if (this.selectedTask) this.build(this.selectedTask);
      }),
    );

    this.subs.add(
      this.websiteAuditService.getAuditTasksLoading().subscribe((v: any) => {
        this.isLoading = v;
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.selectedTask = task;
        this.build(task);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyThemeColors(isDark: boolean) {
    this.isDark = isDark;
    if (isDark) {
      this.green = '#5fcf95';
      this.red = '#e26a5d';
      this.amber = '#e0a544';
      this.gray = '#6b6660';
    } else {
      this.green = '#16a34a';
      this.red = '#d1483b';
      this.amber = '#b9791a';
      this.gray = '#6b7280';
    }
  }

  // ─── Build ───────────────────────────────────────────────────────────────

  private build(task: any) {
    const section = task?.sections?.[SECTION_KEY] || null;
    this.section = section;
    this.compSection = section?.competitor || null;

    const mechanisms = Array.isArray(section?.mechanisms)
      ? section.mechanisms
      : [];
    // The section only tells a story once it carries mechanisms — a queued or
    // failed run has neither those nor a score.
    this.hasData = !!section && mechanisms.length > 0;
    if (!this.hasData) {
      this.mechanisms = [];
      this.visibleMechanisms = [];
      this.selectedMechanism = null;
      this.clusters = [];
      this.topOpportunities = [];
      this.trendHasData = false;
      return;
    }

    this.compName = this.getDomain(
      task?.selectedCompetitor?.domain || task?.selectedCompetitor?.url || '',
    );

    this.score = this.roundOrNull(section?.behavioral_score);
    this.comp = this.roundOrNull(this.compSection?.behavioral_score);
    this.hasComp = this.comp !== null;
    // A competitor score with no competitor name still needs a label.
    if (this.hasComp && !this.compName) this.compName = 'Competitor';

    this.totalCount = mechanisms.length;
    this.activeCount = mechanisms.filter(
      (m: any) => (m?.active_signals || []).length > 0,
    ).length;

    this.description =
      `${this.totalCount} mechanism${this.totalCount === 1 ? '' : 's'} identified as ` +
      `relevant to this store's funnel — which are active, which are untapped.`;

    this.buildBench(task);
    this.buildMechanisms(mechanisms);
    this.buildLargestGap();
    this.buildClusters();
    this.buildOpportunities(section);

    if (this.hasComp) {
      const ahead = (this.score ?? 0) >= (this.comp ?? 0);
      this.leadColor = ahead ? this.green : this.red;
      this.leadText = ahead
        ? `Ahead of ${this.compName}`
        : `Behind ${this.compName}`;
    } else {
      this.leadColor = this.gray;
      this.leadText = 'No competitor tracked';
      this.detailScope = 'target';
    }

    this.applyClusterFilter();
    this.buildTrend(task);
  }

  // Your site + the selected competitor, ranked by score (highest first). The
  // section carries a single competitor block, unlike the multi-competitor
  // score history the other tabs read from.
  private buildBench(task: any) {
    const bars: any[] = [];
    if (this.score !== null) {
      bars.push({
        label: 'Your site',
        score: this.score,
        color: this.accent,
        isYou: true,
      });
    }
    if (this.comp !== null) {
      bars.push({
        label: this.compName,
        score: this.comp,
        color: COMP_COLOR,
      });
    }
    this.benchBars = bars
      .sort((a, b) => b.score - a.score)
      .map((b) => ({ ...b, width: `${Math.max(0, Math.min(100, b.score))}%` }));
  }

  private buildMechanisms(list: any[]) {
    const compList = Array.isArray(this.compSection?.mechanisms)
      ? this.compSection.mechanisms
      : [];
    const claimed = new Set<number>();

    this.mechanisms = list.map((m: any) => {
      const score = this.roundOrNull(m?.score);
      const target = this.roundOrNull(m?.target_score);
      const match = this.matchCompetitorMechanism(m, compList, claimed);
      const compScore = this.roundOrNull(match?.mechanism?.score);
      const lead =
        score !== null && compScore !== null ? score >= compScore : true;

      return {
        raw: m,
        compRaw: match?.mechanism || null,
        name: m?.name || '',
        cluster: m?.cluster || '',
        clusterKey: this.normKey(m?.cluster),
        clusterLabel: this.clusterLabel(m?.cluster),
        description: this.mechanismDesc(m),
        score,
        scoreColor: this.impactColor(score),
        target,
        targetColor: this.impactColor(target),
        gap: score !== null && target !== null ? target - score : null,
        activeCount: (m?.active_signals || []).length,
        untappedCount: (m?.untapped_potential || []).length,
        comp: compScore,
        hasComp: compScore !== null,
        compColor: lead ? this.green : this.red,
        compArrow: lead ? '▲' : '▼',
        selected: false,
      };
    });
  }

  // The two sides name the same mechanism differently ("Trust" vs
  // "Trust / Authority"), so match on the normalized name first and fall back to
  // an unclaimed mechanism from the same cluster.
  private matchCompetitorMechanism(
    m: any,
    compList: any[],
    claimed: Set<number>,
  ): { mechanism: any; index: number } | null {
    const name = this.normKey(m?.name);
    const cluster = this.normKey(m?.cluster);

    for (let i = 0; i < compList.length; i++) {
      if (claimed.has(i)) continue;
      if (name && this.normKey(compList[i]?.name) === name) {
        claimed.add(i);
        return { mechanism: compList[i], index: i };
      }
    }
    for (let i = 0; i < compList.length; i++) {
      if (claimed.has(i)) continue;
      if (cluster && this.normKey(compList[i]?.cluster) === cluster) {
        claimed.add(i);
        return { mechanism: compList[i], index: i };
      }
    }
    return null;
  }

  // Biggest score-to-target gap, labelled with the page its untapped ideas
  // point at most often (e.g. "Scarcity (Product detail page)").
  private buildLargestGap() {
    let top: any = null;
    this.mechanisms.forEach((m) => {
      if (m.gap === null) return;
      if (!top || m.gap > top.gap) top = m;
    });
    if (!top) {
      this.largestGapText = '—';
      return;
    }

    const counts: { [page: string]: number } = {};
    (top.raw?.untapped_potential || []).forEach((u: any) => {
      (u?.pages || []).forEach((p: string) => {
        if (!p) return;
        counts[p] = (counts[p] || 0) + 1;
      });
    });
    const page = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
    this.largestGapText = page ? `${top.name} (${page})` : top.name;
  }

  private buildClusters() {
    const counts: { [key: string]: { label: string; count: number } } = {};
    this.mechanisms.forEach((m) => {
      const key = m.clusterKey;
      if (!key) return;
      if (!counts[key]) counts[key] = { label: m.clusterLabel, count: 0 };
      counts[key].count++;
    });

    this.clusters = [
      {
        key: 'all',
        label: `All (${this.mechanisms.length})`,
        count: this.mechanisms.length,
      },
      ...Object.keys(counts).map((key) => ({
        key,
        label: counts[key].label,
        count: counts[key].count,
      })),
    ];

    // A cluster selected before a competitor switch may no longer exist.
    if (!this.clusters.some((c) => c.key === this.selectedCluster)) {
      this.selectedCluster = 'all';
    }
  }

  private buildOpportunities(section: any) {
    const list = Array.isArray(section?.top_opportunities)
      ? section.top_opportunities
      : [];
    this.topOpportunities = list.map((o: any) => ({
      mechanism: o?.mechanism || '',
      page: o?.page || '',
      impact: this.capitalize(o?.impact),
      impactColor: this.levelColor(o?.impact),
      effort: this.capitalize(o?.effort),
      // Low effort is the good end of that scale, so invert it.
      effortColor: this.levelColor(o?.effort, true),
      rationale: o?.rationale || '',
    }));
  }

  // ─── Cluster filter + selection ──────────────────────────────────────────

  selectCluster(key: string) {
    this.selectedCluster = key;
    this.applyClusterFilter();
  }

  private applyClusterFilter() {
    this.visibleMechanisms =
      this.selectedCluster === 'all'
        ? this.mechanisms
        : this.mechanisms.filter((m) => m.clusterKey === this.selectedCluster);

    // Keep the current selection when it survives the filter, else fall back to
    // the first visible mechanism.
    const keep = this.visibleMechanisms.find(
      (m) => m.name === this.selectedMechanism?.name,
    );
    this.selectMechanism(keep || this.visibleMechanisms[0]);
  }

  selectMechanism(card: any) {
    if (!card) {
      this.selectedMechanism = null;
      return;
    }
    this.mechanisms.forEach((m) => (m.selected = false));
    card.selected = true;

    if (!card.hasComp) this.detailScope = 'target';
    this.selectedMechanism = {
      ...card,
      untapped: this.mapUntapped(this.scopeRaw(card)?.untapped_potential),
      active: this.mapActive(this.scopeRaw(card)?.active_signals),
    };
  }

  setDetailScope(scope: 'target' | 'competitor') {
    if (this.detailScope === scope) return;
    if (scope === 'competitor' && !this.selectedMechanism?.hasComp) return;
    this.detailScope = scope;
    // Re-map the columns from the other side's mechanism block.
    const card = this.mechanisms.find(
      (m) => m.name === this.selectedMechanism?.name,
    );
    if (card) this.selectMechanism(card);
  }

  private scopeRaw(card: any): any {
    return this.detailScope === 'competitor' ? card?.compRaw : card?.raw;
  }

  private mapUntapped(list: any): any[] {
    return (Array.isArray(list) ? list : []).map((u: any) => ({
      title: u?.title || '',
      observation: u?.observation || '',
      rationale: u?.scientific_rationale || '',
      recommendation: u?.recommendation || '',
      pages: Array.isArray(u?.pages) ? u.pages : [],
      ethical: !!u?.ethical_flag,
      expanded: false,
    }));
  }

  private mapActive(list: any): any[] {
    return (Array.isArray(list) ? list : []).map((s: any) => ({
      title: s?.title || '',
      description: s?.description || '',
      pages: Array.isArray(s?.pages) ? s.pages : [],
    }));
  }

  toggleItem(item: any) {
    if (!item) return;
    item.expanded = !item.expanded;
  }

  // ─── Trend ───────────────────────────────────────────────────────────────

  private buildTrend(task: any) {
    const key = SECTION_KEY;
    const hist = task?.overview?.scoreHistory;
    const dates: string[] = hist?.dates || [];
    const tSeries = (hist?.series || []).find((s: any) => s.type === 'target');

    const comps = (hist?.series || [])
      .filter((s: any) => s?.type === 'competitor' && s?.scores?.[key])
      .map((s: any, idx: number) => ({
        series: s,
        key:
          s?.competitorId ||
          this.getDomain(s?.domain || s?.url || s?.name || '') ||
          `comp-${idx}`,
        label:
          this.getDomain(s?.domain || s?.url || s?.name || '') ||
          `Competitor ${idx + 1}`,
        color: COMP_PALETTE[idx % COMP_PALETTE.length],
      }));
    const visibleComps = comps.filter((c: any) => this.isTrendVisible(c.key));

    const header: any[] = [
      'Period',
      'Your site',
      { role: 'tooltip', p: { html: true } },
    ];
    visibleComps.forEach((c: any) => {
      header.push(c.label, { role: 'tooltip', p: { html: true } });
    });

    const rows = dates.map((d, i) => {
      const full = this.fmtDateFull(d);
      const you = this.numOrNull(tSeries?.scores?.[key]?.[i]);
      const compVals = visibleComps.map((c: any) =>
        this.numOrNull(c.series?.scores?.[key]?.[i]),
      );

      let tip =
        `<p class='tooltip-text'><b>${full}</b></p>` +
        `<p class='tooltip-text'><b>Your site: </b>${you !== null ? you : '—'}/100</p>`;
      visibleComps.forEach((c: any, idx: number) => {
        const cv = compVals[idx];
        if (cv !== null) {
          tip += `<p class='tooltip-text'><b>${c.label}: </b>${cv}/100</p>`;
        }
      });

      const row: any[] = [i, you, tip];
      visibleComps.forEach((c: any, idx: number) => {
        const cv = compVals[idx];
        row.push(cv, cv !== null ? tip : null);
      });
      return row;
    });

    // Only plot when the history actually carries this section.
    const hasSeries = (tSeries?.scores?.[key] || []).some(
      (v: any) => typeof v === 'number' && !isNaN(v),
    );

    const n = dates.length;
    this.trendLineChart.dataTable = rows.length ? [header, ...rows] : [];
    this.trendLineChart.options['colors'] = [
      ACCENT,
      ...visibleComps.map((c: any) => c.color),
    ];
    // Hide Google's built-in legend; render our own clickable dash legend.
    this.trendLineChart.options.legend.position = 'none';
    this.trendLegend = [
      {
        key: 'you',
        label: 'Your site',
        color: ACCENT,
        isYou: true,
        visible: true,
      },
      ...comps.map((c: any) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        isYou: false,
        visible: this.isTrendVisible(c.key),
      })),
    ];
    this.trendLineChart.options.hAxis.ticks = dates.map((d, i) => ({
      v: i,
      f: this.fmtShort(d),
    }));
    this.trendLineChart.options.hAxis.viewWindow = {
      min: 0,
      max: n > 1 ? n - 1 : 1,
    };
    this.applyChartTheme();
    // Only a biweekly audit with more than one data point has a meaningful
    // trend — hide the chart for one-time audits and single-point histories.
    this.trendHasData = rows.length > 1 && hasSeries && this.isBiweekly;
    setTimeout(
      () => this.trendLineChart.component?.draw(this.trendLineChart),
      0,
    );
  }

  private isTrendVisible(key: string): boolean {
    return this.trendVisible[key] !== false;
  }

  // Legend click — toggle a competitor line ("Your site" is always shown).
  toggleTrendLine(item: any) {
    if (!item || item.isYou) return;
    this.trendVisible[item.key] = !this.isTrendVisible(item.key);
    this.buildTrend(this.selectedTask);
  }

  private applyChartTheme() {
    const o: any = this.trendLineChart.options;
    o.backgroundColor = 'transparent';
    if (this.isDark) {
      o.legend.textStyle.color = '#cfccc6';
      o.vAxis.gridlineColor = '#3b3a3a';
      o.vAxis.baselineColor = '#3b3a3a';
      o.vAxis.textStyle.color = '#cfccc6';
      o.hAxis.textStyle.color = '#cfccc6';
      o.crosshair.color = '#3b3a3a';
    } else {
      o.legend.textStyle.color = '#666';
      o.vAxis.gridlineColor = '#ededed';
      o.vAxis.baselineColor = '#c0c0c0';
      o.vAxis.textStyle.color = '#666';
      o.hAxis.textStyle.color = '#666';
      o.crosshair.color = '#eeeeee';
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  // One-time audits have no history, so the score-over-time trend is hidden.
  get isBiweekly(): boolean {
    return (this.selectedTask?.auditFrequency || '') === 'biweekly';
  }

  get scopeLabel(): string {
    return this.detailScope === 'competitor' ? this.compName : 'Your site';
  }

  private mechanismDesc(m: any): string {
    return (
      m?.description ||
      MECHANISM_DESC[this.normKey(m?.name)] ||
      CLUSTER_DESC[this.normKey(m?.cluster)] ||
      ''
    );
  }

  // Sentence-case the cluster for the filter chips:
  // "Urgency/Loss" → "Urgency / loss", "Cognitive Ease" → "Cognitive ease".
  private clusterLabel(cluster: string): string {
    const label = (cluster || '')
      .split('/')
      .map((p) => p.trim())
      .filter(Boolean)
      .join(' / ')
      .toLowerCase();
    if (!label) return '';
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private normKey(v: any): string {
    return (v || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  private roundOrNull(v: any): number | null {
    return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
  }

  private numOrNull(v: any): number | null {
    return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
  }

  impactColor(num: number | null): string {
    if (typeof num !== 'number') return this.gray;
    if (num < 50) return this.red;
    if (num < 80) return this.amber;
    return this.green;
  }

  // high/medium/low → red/amber/green (inverted for effort, where low is good).
  private levelColor(level: any, invert = false): string {
    const l = (level || '').toString().toLowerCase();
    if (l === 'high') return invert ? this.red : this.green;
    if (l === 'medium') return this.amber;
    if (l === 'low') return invert ? this.green : this.red;
    return this.gray;
  }

  private capitalize(str: any): string {
    const s = (str || '').toString();
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  rgba(hex: string, a: number): string {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return `rgba(107,102,96,${a})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  private fmtDateFull(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  }

  private fmtShort(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  }
}
